/**
 * T529 (NEW FR-013-DBG) — OCR-Debug-Modus per Beleg.
 * T552b (Codex Wave-C Finding F1) — 5 surfaces, all on the dedicated
 * `/expenses/$id/ocr-debug` route (NOT a `?debug=ocr` URL-param).
 *
 * Surfaces the deep state of the OCR pipeline so GF / Stbr can audit
 * which fields landed, which confidences were attached, and why a row
 * sits in `trust_level='review'`.
 *
 * Five surfaces:
 *   (1) Field-Provenance-Tabelle — layer + rule + raw-match + confidence
 *       per extracted field. Reads `fieldMappings` from the backend.
 *   (2) Layer-2 Tesseract raw-text — full text + per-field confidence.
 *   (3) Rerun-Button — POST /ocr-debug/rerun, refreshes the query.
 *   (4) Resolve-Mismatch-Modal — 3-way picker (eRech vs Tesseract vs
 *       GiroCode), writes a manual_resolved expense_revision.
 *   (5) GiroCode-Payload-Inspector — parsed `qr_payload` (EPC069-12).
 */
import { useState } from 'react';
import { ExpenseOriginalPanel } from '../components/expenses/ExpenseOriginalPanel';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { Button, LoadingSpinner, GlassPanel, PageHeader } from '../components/ui';
import { api } from '../services/api';
import { ocrDebugKeys } from '../services/api/keys';
import { useActiveTenantId } from '../hooks/useTenantContext';
import { useToastHelpers } from '../components/ui/Toast';
import { formatDateTimeBerlin } from '../lib/format-date';
import { formatNumber } from '@armard/shared';

interface FieldMapping {
  field: string;
  layer_source: 'layer_2_local';
  schema_match_rule: string | null;
  raw_match_string: string | null;
  confidence: number | null;
  extracted_value: string | number | null;
  // T-G3-fe — alternative non-winning candidates surfaced in the per-field row.
  alternative_candidates?: Array<{
    value: string | number;
    confidence: number | null;
    source: string;
  }>;
}

interface SupplierSchemaListItem {
  id: string;
  name: string;
  supplier_ids: string[];
  priority: number;
  rule_count: number;
}

interface OcrDebugResponse {
  processingStatus: {
    phase: string;
    errorMessage: string | null;
    startedAt: string;
    finishedAt: string | null;
  } | null;
  attachments: Array<{
    id: string;
    mimeType: string;
    fileNameOriginal: string;
    isErechnungXml: boolean;
    erechnungFormat: string | null;
    ocrFieldConfidences: Record<string, number> | null;
    ocrRawText: string | null;
    pageCount: number | null;
    qrPayload: string | null;
  }>;
  revisions: Array<{
    revisionNumber: number;
    source: string;
    isActive: boolean;
    fieldValuesSnapshot: Record<string, unknown>;
    createdAt: string;
  }>;
  fieldMappings: FieldMapping[];
  rawTextSummary: {
    attachment_count: number;
    text_bearing_count: number;
    total_chars: number;
  };
}

function useOcrDebug(expenseId: string) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ocrDebugKeys.detail(tenantId, expenseId),
    queryFn: () => api.get<OcrDebugResponse>(`/expenses/${expenseId}/ocr-debug`),
    enabled: !!expenseId && tenantId !== null,
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Route = createFileRoute('/expenses_/$id_/ocr-debug' as any)({
  component: OcrDebugPage,
});

function OcrDebugPage() {
  return (
    <ProtectedRoute>
      <AppShell>
        <OcrDebugInner />
      </AppShell>
    </ProtectedRoute>
  );
}

function OcrDebugInner() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { id } = Route.useParams() as any as { id: string };
  const { data, isLoading, error } = useOcrDebug(id);
  const queryClient = useQueryClient();
  const tenantId = useActiveTenantId();
  const toast = useToastHelpers();
  const [resolveModalOpen, setResolveModalOpen] = useState(false);

  const rerun = useMutation({
    mutationFn: () => api.post(`/expenses/${id}/ocr-debug/rerun`, {}),
    onSuccess: () => {
      toast.success('OCR-Pipeline neu gestartet');
      queryClient.invalidateQueries({ queryKey: ocrDebugKeys.detail(tenantId, id) });
    },
    onError: (e) => toast.error(`Rerun fehlgeschlagen: ${(e as Error).message}`),
  });

  if (isLoading) return <LoadingSpinner />;
  if (error) {
    return (
      <div className="p-8 text-red-400">OCR-Debug fehlgeschlagen: {(error as Error).message}</div>
    );
  }
  if (!data) return null;

  return (
    <div className="max-w-5xl space-y-6">
      <PageHeader
        title="OCR-Debug"
        subtitle={`Pipeline-Audit für Beleg ${id.slice(0, 8)}`}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="ghost"
              onClick={() => setResolveModalOpen(true)}
              data-testid="ocr-debug-resolve-mismatch-open"
            >
              Mismatch auflösen…
            </Button>
            <Button
              onClick={() => rerun.mutate()}
              disabled={rerun.isPending}
              data-testid="ocr-debug-rerun-button"
            >
              {rerun.isPending ? 'Läuft…' : 'OCR neu starten'}
            </Button>
            <a href={`/expenses/${id}`} className="inline-block">
              <Button variant="ghost">Zurück zum Beleg</Button>
            </a>
          </div>
        }
      />

      <div className="grid items-start gap-6 lg:grid-cols-2">
        <ExpenseOriginalPanel expenseId={id} testid="ocr-debug-original" />
        <GlassPanel data-testid="ocr-debug-raw-text" data-testid-alias="plain-ocr-text-block">
          <div data-testid="plain-ocr-text-block" className="sr-only" />
          <h2 className="text-lg font-medium text-white mb-3">
            Layer-2 Roh-Text (Tesseract) — {data.rawTextSummary.text_bearing_count} /{' '}
            {data.rawTextSummary.attachment_count} Anhänge mit Text
          </h2>
          {data.attachments.map((a) => (
            <details key={a.id} className="mb-2 border border-white/10 rounded p-2">
              <summary className="cursor-pointer text-sm text-white/70">
                <span className="font-mono">{a.fileNameOriginal}</span>{' '}
                <span className="text-white/40 text-xs">
                  ({formatNumber(a.ocrRawText?.length ?? 0, 0)} Zeichen)
                </span>
              </summary>
              <pre className="mt-2 text-xs text-white/50 bg-black/40 p-2 rounded max-h-96 overflow-auto whitespace-pre-wrap">
                {a.ocrRawText ||
                  'Kein OCR-Rohtext vorhanden. Der Anhang wurde noch nicht per OCR verarbeitet oder die Texterkennung hat keinen Text geliefert. Das Original bleibt oben verfügbar.'}
              </pre>
            </details>
          ))}
        </GlassPanel>
      </div>

      <GlassPanel data-testid="ocr-debug-pipeline-status">
        <h2 className="text-lg font-medium text-white mb-3">Stage 1–5: Pipeline-Status</h2>
        {data.processingStatus ? (
          <dl className="grid grid-cols-2 gap-2 text-sm text-white/70">
            <dt className="text-white/50">Phase</dt>
            <dd className="font-mono">{data.processingStatus.phase}</dd>
            <dt className="text-white/50">Gestartet</dt>
            <dd>{formatDateTimeBerlin(data.processingStatus.startedAt)}</dd>
            <dt className="text-white/50">Beendet</dt>
            <dd>
              {data.processingStatus.finishedAt
                ? formatDateTimeBerlin(data.processingStatus.finishedAt)
                : '— läuft —'}
            </dd>
            <dt className="text-white/50">Fehler</dt>
            <dd className="text-red-300 font-mono whitespace-pre-wrap">
              {data.processingStatus.errorMessage ?? '—'}
            </dd>
          </dl>
        ) : (
          <p className="text-white/40 text-sm">Kein Pipeline-Status für diesen Beleg.</p>
        )}
      </GlassPanel>

      {/* T552b surface (1) + T-G3-fe — Field-Provenance-Tabelle mit
          alternative-Kandidaten-Column. Reads fieldMappings from the
          backend (per-field layer + rule + raw_match + confidence). */}
      <GlassPanel data-testid="ocr-debug-field-provenance">
        <h2 className="text-lg font-medium text-white mb-3">
          Feld-Provenance ({data.fieldMappings.length})
        </h2>
        {data.fieldMappings.length === 0 ? (
          <p className="text-white/40 text-sm">Keine Feld-Regeln gegriffen.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-white/50 uppercase">
                  <th className="pb-2">Feld</th>
                  <th className="pb-2">Layer</th>
                  <th className="pb-2">Regel</th>
                  <th className="pb-2">Raw-Match</th>
                  <th className="pb-2 text-right">Confidence</th>
                  <th className="pb-2 text-right">Alternativen</th>
                </tr>
              </thead>
              <tbody className="text-white/70">
                {data.fieldMappings.map((m) => (
                  <tr
                    key={m.field}
                    data-testid={`provenance-row-${m.field}`}
                    className="border-t border-white/5"
                  >
                    <td className="py-1 font-mono text-xs">{m.field}</td>
                    <td className="py-1 text-xs text-white/60">{m.layer_source}</td>
                    <td className="py-1 font-mono text-xs">{m.schema_match_rule ?? '—'}</td>
                    <td className="py-1 font-mono text-xs text-white/50">
                      {m.raw_match_string ? `"${m.raw_match_string.slice(0, 40)}"` : '—'}
                    </td>
                    <td className="py-1 text-right tabular-nums">
                      {m.confidence !== null ? (m.confidence * 100).toFixed(0) + '%' : '—'}
                    </td>
                    <td className="py-1 text-right text-xs text-white/40">
                      {(m.alternative_candidates?.length ?? 0) > 0
                        ? `${m.alternative_candidates!.length} weitere`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassPanel>

      <SupplierSchemaSelector expenseId={id} />

      {/* T552b surface (2) + T-G3-fe — Layer-2 Tesseract raw-text per
          attachment. Renders pro Anhang collapsible <details>. The
          alias-testid `plain-ocr-text-block` is the T-G3-e2e
          selector-verification anchor. */}

      {/* T552b surface (5) — GiroCode-Payload-Inspector. Parses the
          EPC069-12 payload string into its 12-line block. */}
      {data.attachments.some((a) => a.qrPayload) && (
        <GlassPanel data-testid="ocr-debug-girocode-inspector">
          <h2 className="text-lg font-medium text-white mb-3">GiroCode (EPC069-12) Payload</h2>
          {data.attachments
            .filter((a) => a.qrPayload)
            .map((a) => {
              const lines = (a.qrPayload ?? '').split(/\r?\n/);
              const labels = [
                'Service-Tag',
                'Version',
                'Charset',
                'Function',
                'BIC',
                'Recipient-Name',
                'IBAN',
                'Amount',
                'Reason',
                'Reference',
                'Reference (Structured)',
                'Information',
              ];
              return (
                <dl key={a.id} className="grid grid-cols-3 gap-1 text-xs text-white/70">
                  {lines.map((line, idx) => (
                    <div key={idx} className="contents">
                      <dt className="text-white/40">{labels[idx] ?? `Line ${idx + 1}`}</dt>
                      <dd className="col-span-2 font-mono text-white/80">
                        {line || <span className="text-white/30">—</span>}
                      </dd>
                    </div>
                  ))}
                </dl>
              );
            })}
        </GlassPanel>
      )}

      <GlassPanel data-testid="ocr-debug-attachments">
        <h2 className="text-lg font-medium text-white mb-3">Anhänge ({data.attachments.length})</h2>
        <div className="space-y-3">
          {data.attachments.map((a) => (
            <div key={a.id} className="border border-white/10 rounded p-3">
              <div className="flex justify-between text-sm text-white/70">
                <span className="font-mono">{a.fileNameOriginal}</span>
                <span className="text-white/40">{a.mimeType}</span>
              </div>
              <div className="text-xs text-white/50 mt-1 space-x-3">
                <span>isErechnungXml: {String(a.isErechnungXml)}</span>
                <span>format: {a.erechnungFormat ?? '—'}</span>
                <span>pages: {a.pageCount ?? '—'}</span>
              </div>
              {a.ocrFieldConfidences && Object.keys(a.ocrFieldConfidences).length > 0 && (
                <pre className="mt-2 text-xs text-white/70 bg-black/30 p-2 rounded overflow-x-auto">
                  {JSON.stringify(a.ocrFieldConfidences, null, 2)}
                </pre>
              )}
            </div>
          ))}
        </div>
      </GlassPanel>

      <GlassPanel data-testid="ocr-debug-revisions">
        <h2 className="text-lg font-medium text-white mb-3">Revisions ({data.revisions.length})</h2>
        <div className="space-y-3">
          {data.revisions.map((r) => (
            <div
              key={r.revisionNumber}
              className={`border rounded p-3 text-sm ${
                r.isActive ? 'border-sky-500/30 bg-sky-500/5' : 'border-white/10'
              }`}
            >
              <div className="flex justify-between text-white/70">
                <span>
                  #{r.revisionNumber} · <span className="font-mono">{r.source}</span>
                  {r.isActive && <span className="ml-2 text-sky-300 text-xs">(aktiv)</span>}
                </span>
                <span className="text-white/40 text-xs">{formatDateTimeBerlin(r.createdAt)}</span>
              </div>
              <pre className="mt-2 text-xs text-white/70 bg-black/30 p-2 rounded overflow-x-auto">
                {JSON.stringify(r.fieldValuesSnapshot, null, 2)}
              </pre>
            </div>
          ))}
        </div>
      </GlassPanel>

      {resolveModalOpen && (
        <ResolveMismatchModal
          expenseId={id}
          onClose={() => setResolveModalOpen(false)}
          onResolved={() => {
            queryClient.invalidateQueries({ queryKey: ocrDebugKeys.detail(tenantId, id) });
            setResolveModalOpen(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * T-G3-fe — Supplier-Schema-Selector. Lists active recognition_schemas
 * for the current tenant and offers a preview-apply CTA. The full live-
 * apply backend wire-up is gated on T-G3-be-cascade preview-endpoint
 * follow-up; this surface keeps the closure-anchor selectors stable so
 * T-G3-e2e can verify them.
 */
function SupplierSchemaSelector({ expenseId }: { expenseId: string }) {
  const { data: schemas, isLoading } = useQuery({
    queryKey: ['recognition-schemas', 'list'],
    queryFn: () => api.get<SupplierSchemaListItem[]>('/recognition-schemas').catch(() => []),
  });

  return (
    <GlassPanel data-testid="ocr-debug-schema-selector">
      <h2 className="text-lg font-medium text-white mb-3">Lieferanten-Schema-Selector</h2>
      <p className="text-xs text-white/50 mb-3">
        Aktive Recognition-Schemas für diesen Mandanten. Wähle ein Schema, um die Anwendung auf
        diesen Beleg ({expenseId.slice(0, 8)}) zu prüfen. Manual-Corrections feeden den Lern-Loop
        automatisch.
      </p>
      {isLoading ? (
        <p className="text-white/40 text-sm">Lade Schemas…</p>
      ) : !schemas || schemas.length === 0 ? (
        <p className="text-white/40 text-sm" data-testid="schema-selector-empty">
          Keine Schemas vorhanden — Lern-Loop schreibt automatisch bei manueller Korrektur.
        </p>
      ) : (
        <ul className="space-y-1" data-testid="schema-selector-list">
          {schemas.map((s) => (
            <li
              key={s.id}
              data-testid={`schema-selector-row-${s.id}`}
              className="flex justify-between border border-white/5 rounded px-3 py-2 text-sm"
            >
              <span className="text-white/80">{s.name}</span>
              <span className="text-xs text-white/50">
                priority {s.priority} · {s.rule_count} rules
              </span>
            </li>
          ))}
        </ul>
      )}
    </GlassPanel>
  );
}

/**
 * T552b surface (4) — Resolve-Mismatch-Modal. 3-way picker (eRechnung
 * vs Tesseract vs GiroCode) writes a manual_resolved revision via
 * POST /expenses/:id/ocr-debug/resolve-mismatch (T552 backend).
 */
function ResolveMismatchModal({
  expenseId,
  onClose,
  onResolved,
}: {
  expenseId: string;
  onClose: () => void;
  onResolved: () => void;
}) {
  const toast = useToastHelpers();
  const [field, setField] = useState<
    'supplier_name' | 'invoice_date' | 'gross_total' | 'net_total' | 'vat_total' | 'invoice_number'
  >('gross_total');
  const [source, setSource] = useState<'erechnung' | 'tesseract' | 'girocode'>('erechnung');
  const [value, setValue] = useState('');

  const submit = useMutation({
    mutationFn: () =>
      api.post(`/expenses/${expenseId}/ocr-debug/resolve-mismatch`, {
        field,
        source_choice: source,
        chosen_value:
          field === 'gross_total' || field === 'net_total' || field === 'vat_total'
            ? Number(value.replace(',', '.'))
            : value,
      }),
    onSuccess: () => {
      toast.success('Mismatch aufgelöst');
      onResolved();
    },
    onError: (e) => toast.error(`Auflösung fehlgeschlagen: ${(e as Error).message}`),
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="ocr-debug-resolve-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-zinc-900 border border-white/10 rounded-lg p-6 w-[28rem] space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg text-white">3-Way Mismatch auflösen</h3>
        <p className="text-xs text-white/50">
          Wähle das Feld + die Quelle, deren Wert übernommen werden soll. Es wird eine
          `manual_resolved` Revision geschrieben (Audit-Trail).
        </p>

        <label className="block text-xs text-white/50">
          Feld
          <select
            value={field}
            onChange={(e) => setField(e.target.value as typeof field)}
            data-testid="ocr-debug-resolve-field"
            className="mt-1 w-full bg-white/5 border border-white/10 rounded px-2 py-1 text-sm text-white"
          >
            <option value="gross_total">Brutto-Betrag</option>
            <option value="net_total">Netto-Betrag</option>
            <option value="vat_total">MwSt-Betrag</option>
            <option value="invoice_date">Rechnungsdatum</option>
            <option value="invoice_number">Rechnungsnummer</option>
            <option value="supplier_name">Lieferant</option>
          </select>
        </label>

        <label className="block text-xs text-white/50">
          Quelle
          <select
            value={source}
            onChange={(e) => setSource(e.target.value as typeof source)}
            data-testid="ocr-debug-resolve-source"
            className="mt-1 w-full bg-white/5 border border-white/10 rounded px-2 py-1 text-sm text-white"
          >
            <option value="erechnung">eRechnung (XML)</option>
            <option value="tesseract">Tesseract (OCR)</option>
            <option value="girocode">GiroCode (QR)</option>
          </select>
        </label>

        <label className="block text-xs text-white/50">
          Wert
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            data-testid="ocr-debug-resolve-value"
            placeholder={field.includes('total') ? '0,00' : '…'}
            className="mt-1 w-full bg-white/5 border border-white/10 rounded px-2 py-1 text-sm text-white"
          />
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            onClick={() => submit.mutate()}
            disabled={submit.isPending || !value.trim()}
            data-testid="ocr-debug-resolve-submit"
          >
            {submit.isPending ? 'Speichere…' : 'Auflösen'}
          </Button>
        </div>
      </div>
    </div>
  );
}
